import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { DashboardLayout } from "../../components/DashboardLayout";
import { useClinicLinks } from "./ClinicAdminPages";
import { ActionPromptModal } from "../../components/ActionPromptModal";
import api from "../../lib/api";
import { toast } from "sonner";
import { useCan } from "../../lib/permissions";
import {
  Search, Plus, Pencil, Eye, ArrowLeft, Save, X, RefreshCw, Trash2,
  Briefcase, Clock, IndianRupee, Activity, ShieldCheck, Users,
  CheckCircle2, AlertTriangle, CalendarClock,
} from "lucide-react";

const STATUS_CLASS = {
  active: "mg-status mg-status-active",
  inactive: "mg-status mg-status-inactive",
  deleted: "mg-status mg-status-deleted",
};

// ============================================================
// LIST PAGE
// ============================================================
export function ClinicServicesListPage() {
  const links = useClinicLinks();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [category, setCategory] = useState("");
  const [onlineFilter, setOnlineFilter] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkReasonModal, setBulkReasonModal] = useState(null);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const canCreate = useCan("services.create");
  const canBulk = useCan("services.bulk_actions");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.append("q", q);
      if (statusFilter) params.append("status_filter", statusFilter);
      if (category) params.append("category", category);
      if (onlineFilter) params.append("online_booking", onlineFilter);
      const r = await api.get(`/clinic/services-x?${params.toString()}`);
      setRows(r.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    setLoading(false);
  }, [q, statusFilter, category, onlineFilter]);
  useEffect(() => { load(); }, [load]);

  const toggleSelect = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };

  const runBulk = async (action) => {
    if (!selected.size) return toast.error("No services selected");
    setBulkReasonModal({ action, ids: [...selected] });
  };

  const submitBulk = async (reason) => {
    if (!bulkReasonModal) return;
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      toast.error("Reason is required");
      return;
    }
    setBulkSubmitting(true);
    try {
      const r = await api.post("/clinic/services-x/bulk", {
        service_ids: bulkReasonModal.ids,
        action: bulkReasonModal.action,
        reason: trimmedReason,
      });
      toast.success(`${r.data.ok.length} ok, ${r.data.failed.length} failed`);
      setSelected(new Set());
      setBulkReasonModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk failed");
    }
    setBulkSubmitting(false);
  };

  const exportCsv = () => {
    if (!rows.length) return toast.error("No data");
    const headers = ["service_id", "name", "category", "duration_minutes", "price",
                     "online_booking_enabled", "doctors_count", "appointments_total", "status"];
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      const safe = /^[=+\-@]/.test(s) ? `'${s}` : s;
      return /[",\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
    };
    const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => esc(r[h])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `medigenie-services-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  };

  return (
    <DashboardLayout links={links} title="Services">
      <div className="mg-card mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input className="mg-input pl-9 w-full" placeholder="Search name / category / description…"
              value={q} onChange={(e) => setQ(e.target.value)} data-testid="services-search" />
          </div>
          <input className="mg-input text-sm" placeholder="Category" value={category}
            onChange={(e) => setCategory(e.target.value)} data-testid="services-category-filter" />
          <select className="mg-input text-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="services-status-filter">
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="deleted">Deleted</option>
          </select>
          <select className="mg-input text-sm" value={onlineFilter} onChange={(e) => setOnlineFilter(e.target.value)} data-testid="services-online-filter">
            <option value="">Any online state</option>
            <option value="true">Online booking on</option>
            <option value="false">Online booking off</option>
          </select>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={exportCsv} className="mg-btn-secondary text-sm" data-testid="services-export">CSV</button>
            <button onClick={load} className="mg-btn-secondary text-sm" data-testid="services-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <ManageCategoriesModal />
            {canCreate && (
              <Link to="/clinic-admin/services-x/new" className="mg-btn-primary text-sm" data-testid="add-service-btn">
                <Plus className="w-4 h-4" /> New service
              </Link>
            )}
          </div>
        </div>
      </div>

      {selected.size > 0 && canBulk && (
        <div className="mg-card mb-4 bg-mg-medical/5 border-mg-medical/20" data-testid="service-bulk-bar">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm font-semibold text-mg-navy">{selected.size} selected</div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => runBulk("activate")} className="mg-btn-secondary text-xs" data-testid="svc-bulk-activate">Activate</button>
              <button onClick={() => runBulk("deactivate")} className="mg-btn-secondary text-xs" data-testid="svc-bulk-deactivate">Deactivate</button>
              <button onClick={() => runBulk("soft_delete")} className="text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-700 bg-red-50" data-testid="svc-bulk-delete">Delete</button>
              <button onClick={() => runBulk("restore")} className="mg-btn-secondary text-xs" data-testid="svc-bulk-restore">Restore</button>
              <button onClick={() => setSelected(new Set())} className="text-xs text-slate-500">Cancel</button>
            </div>
          </div>
        </div>
      )}

      <ActionPromptModal
        open={Boolean(bulkReasonModal)}
        title={bulkReasonModal ? `Reason for bulk ${bulkReasonModal.action}` : "Bulk action reason"}
        label="Reason"
        placeholder="Bulk action"
        initialValue="Bulk action"
        confirmLabel="Apply"
        busy={bulkSubmitting}
        onCancel={() => setBulkReasonModal(null)}
        onConfirm={submitBulk}
      />

      <div className="mg-card overflow-x-auto p-0">
        <table className="w-full text-sm" data-testid="services-table">
          <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
            <tr>
              {canBulk && <th className="w-8 px-3 py-3"><input type="checkbox" onChange={(e) => setSelected(e.target.checked ? new Set(rows.map((s) => s.service_id)) : new Set())} data-testid="select-all-services" /></th>}
              <th className="px-2 py-3">Service</th>
              <th>Category</th>
              <th>Duration</th>
              <th className="text-right">Price</th>
              <th className="text-center">Doctors</th>
              <th className="text-center">Bookings</th>
              <th>Online</th>
              <th>Status</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="10" className="text-center text-slate-400 py-8">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan="10" className="text-center text-slate-400 py-8">No services found.</td></tr>}
            {!loading && rows.map((s) => (
              <tr key={s.service_id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`service-row-${s.service_id}`}>
                {canBulk && <td className="px-3 py-3"><input type="checkbox" checked={selected.has(s.service_id)} onChange={() => toggleSelect(s.service_id)} data-testid={`svc-select-${s.service_id}`} /></td>}
                <td className="px-2 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full shrink-0" style={{ background: s.calendar_color || "#2090CF" }} />
                    <div>
                      <Link to={`/clinic-admin/services-x/${s.service_id}`} className="font-bold text-mg-navy hover:text-mg-medical" data-testid={`service-name-${s.service_id}`}>{s.name}</Link>
                      <div className="text-[10px] text-slate-500 line-clamp-1 max-w-xs">{s.description || ""}</div>
                    </div>
                  </div>
                </td>
                <td className="text-xs">{s.category || "—"}</td>
                <td className="text-xs">{s.duration_minutes || 30} min</td>
                <td className="text-right text-xs font-semibold">₹{(s.price || 0).toLocaleString()}</td>
                <td className="text-center text-xs">{s.doctors_count || 0}</td>
                <td className="text-center text-xs">{s.appointments_total || 0}</td>
                <td className="text-xs">{s.online_booking_enabled ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : "—"}</td>
                <td><span className={STATUS_CLASS[s.status] || "mg-status"}>{s.status}</span></td>
                <td className="text-right">
                  <Link to={`/clinic-admin/services-x/${s.service_id}`} className="p-1.5 hover:bg-slate-100 rounded inline-block" data-testid={`svc-view-${s.service_id}`}>
                    <Eye className="w-4 h-4 text-mg-medical" />
                  </Link>
                  <Link to={`/clinic-admin/services-x/${s.service_id}/edit`} className="p-1.5 hover:bg-slate-100 rounded inline-block ml-1" data-testid={`svc-edit-${s.service_id}`}>
                    <Pencil className="w-4 h-4 text-slate-600" />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}

// ============================================================
// DETAIL PAGE
// ============================================================
const DETAIL_TABS = [
  { key: "profile", label: "Profile", icon: Briefcase },
  { key: "doctors", label: "Assigned Doctors", icon: Users },
  { key: "stats", label: "Stats & Revenue", icon: Activity },
  { key: "activity", label: "Activity", icon: ShieldCheck },
];

export function ClinicServiceDetailPage() {
  const links = useClinicLinks();
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("profile");
  const canEdit = useCan("services.edit");
  const canDelete = useCan("services.delete");
  const canRestore = useCan("services.restore");
  const canAssign = useCan("doctor_services.assign");
  const canEditAssignment = useCan("doctor_services.edit");
  const canRemoveAssignment = useCan("doctor_services.remove");

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/clinic/services-x/${id}/detail`);
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    if (!window.confirm("Soft-delete this service?")) return;
    try {
      await api.delete(`/clinic/services-x/${id}`);
      toast.success("Deleted"); nav("/clinic-admin/services-x");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const handleRestore = async () => {
    try {
      await api.post(`/clinic/services-x/${id}/restore`);
      toast.success("Restored"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!data) return <DashboardLayout links={links} title="Service"><div className="text-slate-500">Loading…</div></DashboardLayout>;

  return (
    <DashboardLayout links={links} title="Service">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <button onClick={() => nav("/clinic-admin/services-x")} className="mg-btn-secondary text-sm" data-testid="svc-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex gap-2">
          {canEdit && (
            <Link to={`/clinic-admin/services-x/${id}/edit`} className="mg-btn-primary text-sm" data-testid="svc-edit-link">
              <Pencil className="w-4 h-4" /> Edit
            </Link>
          )}
          {data.status !== "deleted" && canDelete && (
            <button onClick={handleDelete} className="text-sm px-3 py-1.5 rounded-lg border border-red-200 text-red-700 bg-red-50" data-testid="svc-delete-btn">
              <Trash2 className="w-4 h-4 inline" /> Delete
            </button>
          )}
          {data.status === "deleted" && canRestore && (
            <button onClick={handleRestore} className="mg-btn-secondary text-sm" data-testid="svc-restore-btn">Restore</button>
          )}
        </div>
      </div>

      <div className="mg-card mb-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl shrink-0" style={{ background: data.calendar_color || "#2090CF" }} />
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-mg-navy" data-testid="svc-detail-name">{data.name}</h2>
            <div className="text-xs text-slate-500 mt-1">{data.category || "Uncategorized"}</div>
            <div className="text-sm text-slate-600 mt-2 max-w-2xl">{data.description || "No description"}</div>
          </div>
          <div className="text-right text-sm">
            <div className="flex items-center justify-end gap-1 font-semibold text-mg-navy"><IndianRupee className="w-3.5 h-3.5" />{(data.price || 0).toLocaleString()}</div>
            <div className="flex items-center justify-end gap-1 text-slate-500 mt-1"><Clock className="w-3 h-3" />{data.duration_minutes || 30} min</div>
            <span className={`${STATUS_CLASS[data.status] || "mg-status"} mt-2 inline-block`}>{data.status}</span>
          </div>
        </div>
      </div>

      <div className="flex gap-1 border-b border-slate-200 mb-4 overflow-x-auto" data-testid="svc-tabs">
        {DETAIL_TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium flex items-center gap-1.5 border-b-2 -mb-px transition ${tab === t.key ? "border-mg-medical text-mg-medical" : "border-transparent text-slate-500 hover:text-mg-navy"}`}
            data-testid={`svc-tab-${t.key}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "profile" && <ServiceProfileTab data={data} />}
      {tab === "doctors" && (
        <ServiceDoctorsTab serviceId={id} serviceName={data.name} serviceMode={data.service_mode || "in_person"} assignments={data.assignments || []} reload={load}
          canAssign={canAssign} canEditAssignment={canEditAssignment} canRemoveAssignment={canRemoveAssignment} />
      )}
      {tab === "stats" && <ServiceStatsTab data={data} />}
      {tab === "activity" && <ServiceActivityTab data={data} />}
    </DashboardLayout>
  );
}

function ServiceProfileTab({ data }) {
  const rows = [
    ["Category", data.category || "—"],
    ["Duration", `${data.duration_minutes || 30} min`],
    ["Price", `₹${(data.price || 0).toLocaleString()}`],
    ["Tax applicable", data.tax_applicable ? `Yes (${data.tax_rate || 0}%)` : "No"],
    ["Online booking", data.online_booking_enabled ? "Enabled" : "Disabled"],
    ["Display price publicly", data.display_price_publicly ? "Yes" : "No"],
    ["Requires encounter", data.requires_encounter ? "Yes" : "No"],
    ["Advance payment", data.requires_advance_payment ? `Yes (₹${data.advance_payment_amount || 0})` : "No"],
    ["Cancellation allowed", data.cancellation_allowed ? "Yes" : "No"],
    ["Reschedule allowed", data.reschedule_allowed ? "Yes" : "No"],
    ["Buffer between slots", data.buffer_minutes != null ? `${data.buffer_minutes} min` : "—"],
    ["Max bookings/day", data.max_bookings_per_day || "Unlimited"],
  ];
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <div className="mg-card">
        <h3 className="font-bold text-mg-navy mb-3">Booking rules & pricing</h3>
        <table className="w-full text-sm" data-testid="svc-profile-table">
          <tbody>
            {rows.map(([k, v]) => (
              <tr key={k} className="border-b border-slate-50 last:border-0">
                <td className="py-2 text-slate-500">{k}</td>
                <td className="py-2 text-right font-medium text-mg-navy">{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="space-y-4">
        <div className="mg-card">
          <h3 className="font-bold text-mg-navy mb-2 text-sm">Pre-appointment instructions</h3>
          <p className="text-sm text-slate-600 whitespace-pre-wrap">{data.pre_appointment_instructions || "—"}</p>
        </div>
        <div className="mg-card">
          <h3 className="font-bold text-mg-navy mb-2 text-sm">Post-service instructions</h3>
          <p className="text-sm text-slate-600 whitespace-pre-wrap">{data.post_service_instructions || "—"}</p>
        </div>
        <div className="mg-card">
          <h3 className="font-bold text-mg-navy mb-2 text-sm">Internal notes</h3>
          <p className="text-sm text-slate-600 whitespace-pre-wrap">{data.internal_notes || "—"}</p>
        </div>
      </div>
    </div>
  );
}

function ServiceDoctorsTab({ serviceId, serviceName, serviceMode, assignments, reload, canAssign, canEditAssignment, canRemoveAssignment }) {
  const [doctors, setDoctors] = useState([]);
  const [showAssign, setShowAssign] = useState(false);
  const [edit, setEdit] = useState(null); // assignment record being edited
  const [form, setForm] = useState({ doctor_id: "", custom_fee: "", custom_duration_minutes: "", online_booking_enabled: true, notes: "" });
  const [quickBook, setQuickBook] = useState(null); // { doctorId, doctorName, slot }
  const [refreshKey, setRefreshKey] = useState(0);
  const canBook = useCan("appointments.create");
  useEffect(() => {
    if (showAssign && !doctors.length) {
      api.get("/clinic/doctors-x?status_filter=active").then((r) => setDoctors(r.data || []))
        .catch(() => toast.error("Failed to load doctors"));
    }
  }, [showAssign, doctors.length]);

  const assignedIds = new Set(assignments.map((a) => a.doctor_id));
  const availableDoctors = doctors.filter((d) => !assignedIds.has(d.doctor_id));

  const submitAssign = async () => {
    if (!form.doctor_id) return toast.error("Pick a doctor");
    try {
      await api.post(`/clinic/services-x/${serviceId}/doctors`, {
        doctor_id: form.doctor_id,
        custom_fee: form.custom_fee !== "" ? Number(form.custom_fee) : null,
        custom_duration_minutes: form.custom_duration_minutes !== "" ? Number(form.custom_duration_minutes) : null,
        online_booking_enabled: form.online_booking_enabled,
        notes: form.notes || null,
      });
      toast.success("Doctor assigned");
      setShowAssign(false);
      setForm({ doctor_id: "", custom_fee: "", custom_duration_minutes: "", online_booking_enabled: true, notes: "" });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const submitEdit = async () => {
    try {
      await api.patch(`/clinic/services-x/${serviceId}/doctors/${edit.doctor_id}`, {
        custom_fee: edit.custom_fee !== "" && edit.custom_fee != null ? Number(edit.custom_fee) : null,
        custom_duration_minutes: edit.custom_duration_minutes !== "" && edit.custom_duration_minutes != null ? Number(edit.custom_duration_minutes) : null,
        online_booking_enabled: !!edit.online_booking_enabled,
        status: edit.status || "active",
        notes: edit.notes || null,
      });
      toast.success("Updated"); setEdit(null); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (doctor_id) => {
    if (!window.confirm("Remove this doctor from the service?")) return;
    try {
      await api.delete(`/clinic/services-x/${serviceId}/doctors/${doctor_id}`);
      toast.success("Removed"); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="mg-card" data-testid="svc-doctors-tab">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-bold text-mg-navy">Doctors providing this service</h3>
        {canAssign && (
          <button onClick={() => setShowAssign(true)} className="mg-btn-primary text-sm" data-testid="assign-doctor-btn">
            <Plus className="w-4 h-4" /> Assign doctor
          </button>
        )}
      </div>
      {assignments.length === 0 && <div className="text-slate-400 text-sm py-6 text-center">No doctors assigned yet.</div>}
      {assignments.length > 0 && (
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
            <tr><th className="py-2">Doctor</th><th>Specialization</th><th>Custom fee</th><th>Custom duration</th><th>Online</th><th>Status</th><th>Next available</th><th className="text-right">Actions</th></tr>
          </thead>
          <tbody>
            {assignments.map((a) => (
              <tr key={a.doctor_service_id} className="border-b border-slate-50" data-testid={`assignment-${a.doctor_id}`}>
                <td className="py-2 font-semibold text-mg-navy">{a.doctor?.name || a.doctor_id}</td>
                <td className="text-xs">{a.doctor?.specialization || "—"}</td>
                <td className="text-xs">{a.custom_fee != null ? `₹${a.custom_fee}` : <span className="text-slate-400">— base ₹{a.doctor?.consultation_fee || 0}</span>}</td>
                <td className="text-xs">{a.custom_duration_minutes != null ? `${a.custom_duration_minutes} min` : <span className="text-slate-400">— service default</span>}</td>
                <td className="text-xs">{a.online_booking_enabled ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : "—"}</td>
                <td><span className={STATUS_CLASS[a.status] || "mg-status"}>{a.status}</span></td>
                <td className="text-xs"><NextSlotsCell serviceId={serviceId} doctorId={a.doctor_id} active={a.status === "active"}
                  onSelectSlot={canBook ? (slot) => setQuickBook({ doctorId: a.doctor_id, doctorName: a.doctor?.name || a.doctor_id, slot }) : null}
                  refreshKey={refreshKey} /></td>
                <td className="text-right">
                  {canEditAssignment && (
                    <button onClick={() => setEdit({ ...a })} className="p-1.5 hover:bg-slate-100 rounded inline-block" data-testid={`edit-assignment-${a.doctor_id}`}>
                      <Pencil className="w-4 h-4 text-slate-600" />
                    </button>
                  )}
                  {canRemoveAssignment && (
                    <button onClick={() => remove(a.doctor_id)} className="p-1.5 hover:bg-slate-100 rounded inline-block ml-1" data-testid={`remove-assignment-${a.doctor_id}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showAssign && (
        <Modal title="Assign doctor" onClose={() => setShowAssign(false)}>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Doctor *</label>
              <select className="mg-input w-full" value={form.doctor_id} onChange={(e) => setForm({ ...form, doctor_id: e.target.value })} data-testid="assign-doctor-select">
                <option value="">— pick a doctor —</option>
                {availableDoctors.map((d) => (
                  <option key={d.doctor_id} value={d.doctor_id}>{d.name} · {d.specialization || "—"}</option>
                ))}
              </select>
              {availableDoctors.length === 0 && doctors.length > 0 && <div className="text-xs text-slate-400 mt-1">All doctors already assigned.</div>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Custom fee (₹)</label>
                <input className="mg-input" type="number" placeholder="Default" value={form.custom_fee}
                  onChange={(e) => setForm({ ...form, custom_fee: e.target.value })} data-testid="assign-fee" />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Custom duration (min)</label>
                <input className="mg-input" type="number" placeholder="Default" value={form.custom_duration_minutes}
                  onChange={(e) => setForm({ ...form, custom_duration_minutes: e.target.value })} data-testid="assign-duration" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.online_booking_enabled}
                onChange={(e) => setForm({ ...form, online_booking_enabled: e.target.checked })} />
              Available for online booking
            </label>
            <textarea className="mg-input w-full" rows={2} placeholder="Notes (optional)"
              value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div className="flex gap-2 mt-5">
            <button onClick={() => setShowAssign(false)} className="mg-btn-secondary flex-1 justify-center">Cancel</button>
            <button onClick={submitAssign} className="mg-btn-primary flex-1 justify-center" data-testid="confirm-assign">Assign</button>
          </div>
        </Modal>
      )}

      {edit && (
        <Modal title={`Edit assignment — ${edit.doctor?.name || edit.doctor_id}`} onClose={() => setEdit(null)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Custom fee (₹)</label>
                <input className="mg-input" type="number" placeholder="Default" value={edit.custom_fee ?? ""}
                  onChange={(e) => setEdit({ ...edit, custom_fee: e.target.value })} data-testid="edit-assignment-fee" />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Custom duration (min)</label>
                <input className="mg-input" type="number" placeholder="Default" value={edit.custom_duration_minutes ?? ""}
                  onChange={(e) => setEdit({ ...edit, custom_duration_minutes: e.target.value })} data-testid="edit-assignment-duration" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!edit.online_booking_enabled}
                onChange={(e) => setEdit({ ...edit, online_booking_enabled: e.target.checked })} />
              Available for online booking
            </label>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Status</label>
              <select className="mg-input w-full" value={edit.status || "active"} onChange={(e) => setEdit({ ...edit, status: e.target.value })}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <textarea className="mg-input w-full" rows={2} placeholder="Notes"
              value={edit.notes || ""} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} />
          </div>
          <div className="flex gap-2 mt-5">
            <button onClick={() => setEdit(null)} className="mg-btn-secondary flex-1 justify-center">Cancel</button>
            <button onClick={submitEdit} className="mg-btn-primary flex-1 justify-center" data-testid="confirm-edit-assignment">Save</button>
          </div>
        </Modal>
      )}

      {quickBook && (
        <QuickBookModal
          serviceId={serviceId}
          serviceName={serviceName}
          serviceMode={serviceMode}
          doctorId={quickBook.doctorId}
          doctorName={quickBook.doctorName}
          slot={quickBook.slot}
          onClose={() => setQuickBook(null)}
          onBooked={() => { setQuickBook(null); setRefreshKey((k) => k + 1); reload(); }}
        />
      )}
    </div>
  );
}

function ServiceStatsTab({ data }) {
  const s = data.appointment_stats || {};
  const cards = [
    { label: "Total appointments", value: s.total || 0, color: "text-mg-medical" },
    { label: "Completed", value: s.completed || 0, color: "text-emerald-600" },
    { label: "Cancelled", value: s.cancelled || 0, color: "text-red-500" },
    { label: "Revenue", value: `₹${(data.revenue || 0).toLocaleString()}`, color: "text-mg-navy" },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="svc-stats-tab">
      {cards.map((c) => (
        <div key={c.label} className="mg-card">
          <div className="text-xs text-slate-500">{c.label}</div>
          <div className={`text-3xl font-bold mt-1 ${c.color}`}>{c.value}</div>
        </div>
      ))}
    </div>
  );
}

function ServiceActivityTab({ data }) {
  const rows = data.activity || [];
  if (!rows.length) return <div className="mg-card text-slate-400 text-sm">No activity yet.</div>;
  return (
    <div className="mg-card overflow-x-auto" data-testid="svc-activity-tab">
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
          <tr><th className="py-2">When</th><th>Action</th><th>By</th><th>Notes</th></tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-50">
              <td className="py-2 text-xs">{new Date(r.created_at || r.timestamp).toLocaleString()}</td>
              <td className="text-xs font-medium">{r.action}</td>
              <td className="text-xs text-slate-500">{r.actor_user_id || "—"}</td>
              <td className="text-xs text-slate-500">{r.description || ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NextSlotsCell({ serviceId, doctorId, active, onSelectSlot, refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    if (!active) { setLoading(false); return; }
    setLoading(true);
    api.get(`/clinic/services-x/${serviceId}/doctors/${doctorId}/next-slots?limit=3&days_ahead=14`)
      .then((r) => { if (!cancelled) { setData(r.data); setLoading(false); } })
      .catch(() => { if (!cancelled) { setData({ slots: [], reason: "error" }); setLoading(false); } });
    return () => { cancelled = true; };
  }, [serviceId, doctorId, active, refreshKey]);

  if (!active) return <span className="text-slate-400">—</span>;
  if (loading) return <span className="text-slate-400">…</span>;
  const slots = data?.slots || [];
  if (slots.length === 0) {
    const label = ({ off_day: "Off this week", on_leave: "On leave", fully_booked: "Fully booked",
                     doctor_not_assigned: "Not assigned", no_slots: "No slots" })[data?.reason] || "No availability";
    return <span className="text-amber-600 flex items-center gap-1" data-testid={`next-slots-empty-${doctorId}`}><AlertTriangle className="w-3 h-3" />{label}</span>;
  }
  const fmt = (iso) => {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  };
  return (
    <div className="flex flex-col gap-0.5" data-testid={`next-slots-${doctorId}`}>
      {slots.map((s, i) => {
        const Chip = onSelectSlot ? "button" : "span";
        return (
          <Chip key={i}
            {...(onSelectSlot ? {
              onClick: () => onSelectSlot(s),
              type: "button",
              "data-testid": `book-slot-${doctorId}-${i}`,
              className: "inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50 px-1 -mx-1 rounded transition text-left",
            } : { className: "inline-flex items-center gap-1 text-emerald-700" })}
          >
            <CalendarClock className="w-3 h-3" />{fmt(s.start)}
          </Chip>
        );
      })}
    </div>
  );
}

function QuickBookModal({ serviceId, serviceName, serviceMode, doctorId, doctorName, slot, onClose, onBooked }) {
  const [mode, setMode] = useState("existing"); // existing | new
  const [patientQ, setPatientQ] = useState("");
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [newPatient, setNewPatient] = useState({ name: "", phone: "", email: "", gender: "" });
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [consultationMode, setConsultationMode] = useState("");

  // Debounced patient search
  useEffect(() => {
    if (mode !== "existing") return;
    const t = setTimeout(() => {
      const q = patientQ.trim();
      if (q.length < 2) { setPatients([]); return; }
      api.get(`/clinic/patients?q=${encodeURIComponent(q)}`)
        .then((r) => setPatients((r.data || []).slice(0, 8)))
        .catch(() => setPatients([]));
    }, 250);
    return () => clearTimeout(t);
  }, [patientQ, mode]);

  const submit = async () => {
    if (mode === "existing" && !selectedPatient) return toast.error("Pick a patient");
    if (mode === "new" && (!newPatient.name || !newPatient.phone)) return toast.error("Name and phone required");
    setSubmitting(true);
    try {
      const payload = {
        doctor_id: doctorId, service_id: serviceId, consultation_mode: consultationMode || null,
        appointment_datetime: slot.start, notes: notes || null,
        ...(mode === "existing"
          ? { patient_id: selectedPatient.patient_id }
          : { new_patient: { ...newPatient, email: newPatient.email || null } }),
      };
      await api.post("/appointments", payload);
      toast.success("Appointment booked");
      onBooked();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to book");
    }
    setSubmitting(false);
  };

  const fmtSlot = new Date(slot.start).toLocaleString(undefined,
    { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

  return (
    <Modal title="Book appointment" onClose={onClose}>
      <div className="bg-mg-medical/5 border border-mg-medical/20 rounded-lg p-3 mb-4 text-sm" data-testid="quickbook-summary">
        <div className="font-semibold text-mg-navy">{serviceName}</div>
        <div className="text-slate-600 mt-1">with <span className="font-medium">{doctorName}</span></div>
        <div className="text-mg-medical font-semibold mt-1 flex items-center gap-1"><CalendarClock className="w-4 h-4" />{fmtSlot}</div>
      </div>

      <div className="flex gap-1 mb-3 text-sm border-b border-slate-200">
        <button type="button" onClick={() => setMode("existing")}
          className={`px-3 py-1.5 border-b-2 -mb-px ${mode === "existing" ? "border-mg-medical text-mg-medical font-semibold" : "border-transparent text-slate-500"}`}
          data-testid="qb-tab-existing">Existing patient</button>
        <button type="button" onClick={() => setMode("new")}
          className={`px-3 py-1.5 border-b-2 -mb-px ${mode === "new" ? "border-mg-medical text-mg-medical font-semibold" : "border-transparent text-slate-500"}`}
          data-testid="qb-tab-new">New patient</button>
      </div>

      {mode === "existing" && (
        <div className="space-y-2">
          <input className="mg-input w-full" placeholder="Search by name / phone / email…"
            value={patientQ} onChange={(e) => { setPatientQ(e.target.value); setSelectedPatient(null); }}
            data-testid="qb-patient-search" />
          {selectedPatient ? (
            <div className="flex items-center justify-between border border-emerald-200 bg-emerald-50 rounded p-2 text-sm">
              <div>
                <div className="font-semibold text-mg-navy">{selectedPatient.name}</div>
                <div className="text-xs text-slate-500">{selectedPatient.phone || "—"} · {selectedPatient.email || "—"}</div>
              </div>
              <button type="button" onClick={() => setSelectedPatient(null)} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
            </div>
          ) : (
            patients.length > 0 && (
              <div className="border border-slate-200 rounded max-h-48 overflow-y-auto">
                {patients.map((p) => (
                  <button key={p.patient_id} type="button" onClick={() => setSelectedPatient(p)}
                    className="block w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0 text-sm"
                    data-testid={`qb-patient-${p.patient_id}`}>
                    <div className="font-semibold text-mg-navy">{p.name}</div>
                    <div className="text-xs text-slate-500">{p.phone || "—"} · {p.email || "—"}</div>
                  </button>
                ))}
              </div>
            )
          )}
        </div>
      )}

      {mode === "new" && (
        <div className="space-y-2">
          <input className="mg-input w-full" placeholder="Patient name *"
            value={newPatient.name} onChange={(e) => setNewPatient({ ...newPatient, name: e.target.value })} data-testid="qb-new-name" />
          <input className="mg-input w-full" placeholder="Phone *"
            value={newPatient.phone} onChange={(e) => setNewPatient({ ...newPatient, phone: e.target.value })} data-testid="qb-new-phone" />
          <input className="mg-input w-full" placeholder="Email (optional)"
            value={newPatient.email} onChange={(e) => setNewPatient({ ...newPatient, email: e.target.value })} data-testid="qb-new-email" />
          <select className="mg-input w-full" value={newPatient.gender}
            onChange={(e) => setNewPatient({ ...newPatient, gender: e.target.value })} data-testid="qb-new-gender">
            <option value="">Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>
      )}

      <textarea className="mg-input w-full mt-3" rows={2} placeholder="Notes (optional)"
        value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="qb-notes" />
      {serviceMode === "hybrid" && <select className="mg-input w-full mt-3" value={consultationMode} onChange={(e)=>setConsultationMode(e.target.value)} required>
        <option value="">Choose consultation type</option><option value="in_person">In person</option><option value="online">Online</option>
      </select>}

      <div className="flex gap-2 mt-5">
        <button onClick={onClose} className="mg-btn-secondary flex-1 justify-center">Cancel</button>
        <button onClick={submit} disabled={submitting || (serviceMode === "hybrid" && !consultationMode)} className="mg-btn-primary flex-1 justify-center" data-testid="qb-confirm">
          {submitting ? "Booking…" : "Book appointment"}
        </button>
      </div>
    </Modal>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-mg-navy text-lg">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ============================================================
// EDIT / CREATE PAGE
// ============================================================
const EMPTY = {
  name: "", category: "", description: "",
  service_mode: "in_person",
  duration_minutes: 30, price: 0,
  tax_applicable: false, tax_rate: "",
  calendar_color: "#2090CF",
  online_booking_enabled: true,
  display_price_publicly: true,
  requires_encounter: true,
  requires_advance_payment: false,
  advance_payment_amount: "",
  cancellation_allowed: true,
  reschedule_allowed: true,
  buffer_minutes: "",
  max_bookings_per_day: "",
  pre_appointment_instructions: "",
  post_service_instructions: "",
  internal_notes: "",
};

function ManageCategoriesModal({ onChanged, compact = false }) {
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({ name: "", description: "" });
  const [editing, setEditing] = useState(null);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [categoryError, setCategoryError] = useState("");
  
  const load = () => {
    setLoadingCategories(true); setCategoryError("");
    return api.get("/clinic/service-categories?include_archived=true")
      .then(r => setCategories(r.data))
      .catch(() => setCategoryError("Categories could not be loaded."))
      .finally(() => setLoadingCategories(false));
  };
  useEffect(() => { if (open) load(); }, [open]);

  const save = async () => {
    try {
      if (editing) await api.patch(`/clinic/service-categories/${editing}`, form);
      else await api.post("/clinic/service-categories", form);
      toast.success(editing ? "Category updated" : "Category created");
      setForm({ name: "", description: "" });
      setEditing(null); load(); onChanged?.();
    } catch(e) { toast.error(e?.response?.data?.detail || "Failed to save category"); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/clinic/service-categories/${id}`);
      toast.success("Category archived; linked services are now Uncategorized");
      load(); onChanged?.();
    } catch(e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
  };

  const restore = async (id) => {
    try { await api.post(`/clinic/service-categories/${id}/restore`); toast.success("Category restored"); load(); onChanged?.(); }
    catch(e) { toast.error(e?.response?.data?.detail || "Failed to restore"); }
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="mg-btn-secondary text-sm">{compact ? "Manage" : "Categories"}</button>
      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[80vh] flex flex-col" role="dialog" aria-modal="true" aria-labelledby="category-modal-title">
            <div className="flex justify-between items-center mb-4">
              <h3 id="category-modal-title" className="font-bold text-mg-navy">Manage Categories</h3>
              <button type="button" aria-label="Close category manager" onClick={() => setOpen(false)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="flex gap-2 mb-4">
              <input aria-label="Category name" className="mg-input flex-1" placeholder="Category Name" value={form.name} onChange={e=>setForm({...form, name: e.target.value})} />
              <button type="button" onClick={save} className="mg-btn-primary">{editing ? "Save" : "Add"}</button>
              {editing && <button type="button" onClick={()=>{setEditing(null);setForm({name:"",description:""});}} className="mg-btn-secondary">Cancel</button>}
            </div>
            <div className="flex-1 overflow-y-auto space-y-2">
              {loadingCategories && <div className="text-sm text-slate-500 text-center py-4">Loading categories…</div>}
              {!loadingCategories && categoryError && <div role="alert" className="text-sm text-red-600 text-center py-4">{categoryError} <button type="button" onClick={load} className="underline font-semibold">Retry</button></div>}
              {categories.map(c => (
                <div key={c.category_id} className={`flex justify-between items-center p-3 border rounded-xl ${c.status !== "active" ? "opacity-60" : ""}`}>
                  <div>
                    <div className="font-bold text-mg-navy text-sm">{c.name}</div>
                  </div>
                  <div className="flex gap-2">
                    {c.status === "active" && !c.system && <button type="button" onClick={()=>{setEditing(c.category_id);setForm({name:c.name,description:c.description||""});}} className="text-mg-medical text-xs">Edit</button>}
                    {c.status === "active" && !c.system && <button type="button" onClick={() => remove(c.category_id)} className="text-red-500 hover:text-red-700" title="Archive"><Trash2 className="w-4 h-4" /></button>}
                    {c.status !== "active" && <button type="button" onClick={()=>restore(c.category_id)} className="text-emerald-700 text-xs">Restore</button>}
                  </div>
                </div>
              ))}
              {!loadingCategories && !categoryError && categories.length === 0 && <div className="text-sm text-slate-500 text-center py-4">No categories found</div>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function ClinicServiceEditPage() {
  const links = useClinicLinks();
  const { id } = useParams();
  const nav = useNavigate();
  const isEdit = !!id;
  const [form, setForm] = useState(EMPTY);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadCategories = () => api.get("/clinic/service-categories").then(r => setCategories(r.data)).catch(() => {});

  useEffect(() => {
    // Load categories
    loadCategories();

    if (!isEdit) {
      setLoading(false);
      return;
    }
    api.get(`/clinic/services-x/${id}/detail`).then((r) => {
      const d = r.data;
      setForm({
        ...EMPTY, ...d,
        tax_rate: d.tax_rate ?? "",
        advance_payment_amount: d.advance_payment_amount ?? "",
        buffer_minutes: d.buffer_minutes ?? "",
        max_bookings_per_day: d.max_bookings_per_day ?? "",
      });
      setLoading(false);
    }).catch((e) => { toast.error(e?.response?.data?.detail || "Failed"); setLoading(false); });
  }, [id, isEdit]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name) return toast.error("Name is required");
    setSaving(true);
    try {
      const payload = {
        ...form,
        duration_minutes: Number(form.duration_minutes) || 30,
        price: Number(form.price) || 0,
        tax_rate: form.tax_rate === "" ? null : Number(form.tax_rate),
        advance_payment_amount: form.advance_payment_amount === "" ? null : Number(form.advance_payment_amount),
        buffer_minutes: form.buffer_minutes === "" ? null : Number(form.buffer_minutes),
        max_bookings_per_day: form.max_bookings_per_day === "" ? null : Number(form.max_bookings_per_day),
      };
      if (isEdit) {
        await api.patch(`/clinic/services-x/${id}`, payload);
        toast.success("Updated");
        nav(`/clinic-admin/services-x/${id}`);
      } else {
        const r = await api.post("/clinic/services-x", payload);
        toast.success("Created");
        nav(`/clinic-admin/services-x/${r.data.service_id}`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  if (loading) return <DashboardLayout links={links} title="Service"><div className="text-slate-500">Loading…</div></DashboardLayout>;

  return (
    <DashboardLayout links={links} title={isEdit ? "Edit service" : "New service"}>
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => nav(-1)} className="mg-btn-secondary text-sm" data-testid="svc-edit-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button onClick={submit} disabled={saving} className="mg-btn-primary text-sm" data-testid="svc-save">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="mg-card">
          <h3 className="font-bold text-mg-navy mb-3">Basics</h3>
          <Field label="Name *">
            <input className="mg-input w-full" value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="svc-form-name" />
          </Field>
          <Field label="Category">
            <div className="flex gap-2">
              <select className="mg-input w-full" value={form.category_id || ""} onChange={(e) => update("category_id", e.target.value)} data-testid="svc-form-category">
                {categories.map(c => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
              </select>
              <ManageCategoriesModal compact onChanged={loadCategories} />
            </div>
          </Field>
          <Field label="Description">
            <textarea className="mg-input w-full" rows={3} value={form.description || ""} onChange={(e) => update("description", e.target.value)} data-testid="svc-form-desc" />
          </Field>
          <Field label="Consultation mode">
            <select className="mg-input w-full" value={form.service_mode || "in_person"} onChange={(e) => update("service_mode", e.target.value)} data-testid="svc-form-mode">
              <option value="in_person">In person</option><option value="online">Online</option><option value="hybrid">Hybrid (patient chooses)</option>
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Duration">
              <div className="flex gap-2">
                <input className="mg-input w-full" type="number" placeholder="Hrs" min="0" value={Math.floor((form.duration_minutes || 0) / 60) || ""} onChange={(e) => {
                  const hrs = parseInt(e.target.value) || 0;
                  const mins = (form.duration_minutes || 0) % 60;
                  update("duration_minutes", hrs * 60 + mins);
                }} />
                <input className="mg-input w-full" type="number" placeholder="Mins" min="0" max="59" value={(form.duration_minutes || 0) % 60 || ""} onChange={(e) => {
                  const hrs = Math.floor((form.duration_minutes || 0) / 60);
                  const mins = parseInt(e.target.value) || 0;
                  update("duration_minutes", hrs * 60 + mins);
                }} />
              </div>
            </Field>
            <Field label="Price (₹)">
              <input className="mg-input w-full" type="number" value={form.price} onChange={(e) => update("price", e.target.value)} data-testid="svc-form-price" />
            </Field>
          </div>
          <Field label="Calendar colour">
            <input type="color" className="mg-input w-full h-10" value={form.calendar_color || "#2090CF"}
              onChange={(e) => update("calendar_color", e.target.value)} data-testid="svc-form-color" />
          </Field>
        </div>

        <div className="mg-card">
          <h3 className="font-bold text-mg-navy mb-3">Tax & advance</h3>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!form.tax_applicable} onChange={(e) => update("tax_applicable", e.target.checked)} data-testid="svc-form-tax-toggle" />
            Tax applicable
          </label>
          {form.tax_applicable && (
            <Field label="Tax rate (%)">
              <input className="mg-input w-full" type="number" value={form.tax_rate} onChange={(e) => update("tax_rate", e.target.value)} data-testid="svc-form-tax-rate" />
            </Field>
          )}
          <label className="flex items-center gap-2 text-sm mt-3">
            <input type="checkbox" checked={!!form.requires_advance_payment} onChange={(e) => update("requires_advance_payment", e.target.checked)} data-testid="svc-form-advance-toggle" />
            Requires advance payment
          </label>
          {form.requires_advance_payment && (
            <Field label="Advance amount (₹)">
              <input className="mg-input w-full" type="number" value={form.advance_payment_amount} onChange={(e) => update("advance_payment_amount", e.target.value)} data-testid="svc-form-advance" />
            </Field>
          )}
          <h3 className="font-bold text-mg-navy mt-5 mb-3">Booking rules</h3>
          <label className="flex items-center gap-2 text-sm mb-2">
            <input type="checkbox" checked={!!form.online_booking_enabled} onChange={(e) => update("online_booking_enabled", e.target.checked)} data-testid="svc-form-online" />
            Available for online booking
          </label>
          <label className="flex items-center gap-2 text-sm mb-2">
            <input type="checkbox" checked={!!form.display_price_publicly} onChange={(e) => update("display_price_publicly", e.target.checked)} />
            Display price publicly
          </label>
          <label className="flex items-center gap-2 text-sm mb-2">
            <input type="checkbox" checked={!!form.requires_encounter} onChange={(e) => update("requires_encounter", e.target.checked)} />
            Requires encounter
          </label>
          <label className="flex items-center gap-2 text-sm mb-2">
            <input type="checkbox" checked={!!form.cancellation_allowed} onChange={(e) => update("cancellation_allowed", e.target.checked)} />
            Allow cancellation
          </label>
          <label className="flex items-center gap-2 text-sm mb-2">
            <input type="checkbox" checked={!!form.reschedule_allowed} onChange={(e) => update("reschedule_allowed", e.target.checked)} />
            Allow reschedule
          </label>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <Field label="Buffer (min)">
              <input className="mg-input w-full" type="number" value={form.buffer_minutes} onChange={(e) => update("buffer_minutes", e.target.value)} />
            </Field>
            <Field label="Max bookings/day">
              <input className="mg-input w-full" type="number" value={form.max_bookings_per_day} onChange={(e) => update("max_bookings_per_day", e.target.value)} />
            </Field>
          </div>
        </div>

        <div className="mg-card lg:col-span-2">
          <h3 className="font-bold text-mg-navy mb-3">Patient-facing instructions</h3>
          <Field label="Pre-appointment instructions">
            <textarea className="mg-input w-full" rows={3} value={form.pre_appointment_instructions || ""} onChange={(e) => update("pre_appointment_instructions", e.target.value)} data-testid="svc-form-pre" />
          </Field>
          <Field label="Post-service instructions">
            <textarea className="mg-input w-full" rows={3} value={form.post_service_instructions || ""} onChange={(e) => update("post_service_instructions", e.target.value)} data-testid="svc-form-post" />
          </Field>
          <Field label="Internal notes (staff only)">
            <textarea className="mg-input w-full" rows={2} value={form.internal_notes || ""} onChange={(e) => update("internal_notes", e.target.value)} data-testid="svc-form-internal" />
          </Field>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-3">
      <label className="text-xs text-slate-500 mb-1 block">{label}</label>
      {children}
    </div>
  );
}
